import {Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {PreviewDataSourceInfo} from '../../../../../data-source/models/preview-data-source-info.model';
import {PreviewColumns} from '../../../../../data-source/models/preview-columns.model';
import {DataSourceRowNumber} from '../../../../../data-source/models/data-source-row-number.type';
import {ColumnsType} from '../../../../../data-source/models/columns-type.model';
import {combineLatest, Subscription} from 'rxjs';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import {tap} from 'rxjs/operators';
import {IDataSourcePreview} from '../../interfaces/i-data-source-preview';
import {ConfirmationReplacementComponent}
  from '../../../../../data-source/components/dialogs/confirmation-replacement/confirmation-replacement.component';
import {MatMenuTrigger} from "@angular/material/menu";
import { DataSourceUploadProgressService } from '../../../../../data-source/services/data-source-upload-progress.service';

@Component({
  selector: 'morva-preview-excel',
  templateUrl: './preview-excel.component.html',
  styleUrls: ['./preview-excel.component.scss'],
})
export class PreviewExcelComponent implements OnInit, OnDestroy {
  @ViewChild('menuTrigger') menu: MatMenuTrigger;
  @ViewChild('searchType') search: ElementRef;
  workspaceId: string;
  loading: boolean;
  type: 'CreateDataSource' | 'ReplacementDataSource';
  oldDataSourceName: string;
  previewDataSourceInfo: PreviewDataSourceInfo;
  columns: PreviewColumns;
  rowNumber: DataSourceRowNumber;
  data: any;
  isUploading = false;
  columnsTypes: ColumnsType;
  changedColumnsTypes: ColumnsType;
  showColumnsTypes: ColumnsType;
  observables = [];
  hundred: DataSourceRowNumber = 'Hundred';
  twoHundred: DataSourceRowNumber = 'TwoHundred';
  thousand: DataSourceRowNumber = 'Thousand';
  subscriptions: Subscription[] = [];
  columnsDataLength: number[] = [];
  isEmptySheet = false;
  types = [
    {
      id: 0,
      type: 'WholeNumber',
    },
    {
      id: 1,
      type: 'DecimalNumber',
    },
    {
      id: 2,
      type: 'Currency',
    },
    {
      id: 3,
      type: 'Date',
    },
    {
      id: 12,
      type: 'JalaliDate'
    },
    {
      id: 4,
      type: 'Boolean',
    },
    {
      id: 5,
      type: 'Text',
    },
    {
      id: 6,
      type: 'Binary',
    },
    {
      id: 7,
      type: 'Variant',
    },
  ];
  sheetNames: string[];
  selectedSheetName: string;
  hasError: boolean;
  loadingSheetNames: boolean;

  constructor(
    private dialogRef: NbDialogRef<any>,
    private dialogService: NbDialogService,
    @Inject('DataSourcePreview') private dataSourcePreviewFacade: IDataSourcePreview,
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.setSubscribers();
  }

  loadTableContent(rowNumber: DataSourceRowNumber): void {
    this.rowNumber = rowNumber;
    this.loading = true;
    if(this.type === 'CreateDataSource') {
      this.dataSourcePreviewFacade
          .loadFileOrURLPreviewTableContent(this.workspaceId,this.previewDataSourceInfo,rowNumber,this.selectedSheetName);
    } else if (this.type === 'ReplacementDataSource') {
      this.dataSourcePreviewFacade.checkUpdateDataSourceData(this.workspaceId,this.previewDataSourceInfo,
          rowNumber,this.selectedSheetName);
    }
  }

  setSubscribers(): void {
    this.observables.push(this.dataSourcePreviewFacade.getWorkspaceId$().
    pipe(tap(workspaceId => this.workspaceId = workspaceId)));
    this.observables.push(this.dataSourcePreviewFacade.getPreviewDataSourceInfo$().
    pipe(tap(previewDataSourceInfo => this.previewDataSourceInfo = previewDataSourceInfo)));

    this.subscriptions.push(combineLatest(this.observables).subscribe(() => {
      this.loadingSheetNames = true;
      this.dataSourcePreviewFacade
        .loadExcelSheetsNamesPreview(this.previewDataSourceInfo.dataSourceId,this.previewDataSourceInfo.filePath);
    }));

    this.subscriptions.push(this.dataSourcePreviewFacade.getExcelSheetNames$().subscribe(
      (sheetNames) => {
        this.loadingSheetNames = false;
        this.sheetNames = sheetNames;
        this.selectedSheetName = sheetNames[0];
        this.loadTableContent(this.hundred);
      },
    ));

    this.subscriptions.push(this.dataSourcePreviewFacade.getPreviewTableContent$().subscribe(
      (columns: PreviewColumns) => {
        this.loading = false;
        this.columns = columns;
        if(columns) {
          if (Object.keys(columns.data).length === 0) {
            this.columnsDataLength = [];
            this.columnsTypes = [];
            this.isEmptySheet = true;
          } else {
            this.setRowLength(columns.data);
            this.setColumnsType(columns.data);
            this.isEmptySheet = false;
          }
        }
      },
    ));

    this.subscriptions.push(this.dataSourcePreviewFacade.getSubmitState$().subscribe(
      (submit) => {
        if(submit === 'submitted') {
          this.isUploading = false;
          this.dataSourcePreviewFacade.setSubmitState('notSubmitted');
          this.dialogRef.close();
        } else if (submit === 'notSubmitted') {
          this.isUploading = false;
        }
      },
    ));

    this.subscriptions.push(this.dataSourcePreviewFacade.getErrorTableContent$().subscribe(
      (hasError) => {
        this.hasError = hasError;
      },
    ));
  }

  deletePreviewConnection(): void {
    if(this.type === 'CreateDataSource') {
      this.dataSourcePreviewFacade.deletePreviewConnection(this.previewDataSourceInfo);
    } else {
      this.dataSourcePreviewFacade.deletePreviewReplacementConnection(this.previewDataSourceInfo);
    }
  }

  changeSheet(sheetName: string): void {
    this.selectedSheetName = sheetName;
    this.columns = null;
    this.loadTableContent(this.hundred);
  }

  setColumnType(newValue, key): void {
    this.changedColumnsTypes[key] = newValue;
  }

  openMenuDataType(): void {
    this.search.nativeElement.value = '';
    this.showColumnsTypes = [];
    for(const key in this.columnsTypes) {
      if(key) {
        this.showColumnsTypes[key] = this.columnsTypes[key];
      }
    }
  }

  searchInColumnsTypes(searchKey: string): void {
    this.showColumnsTypes = [];
    for(const key in this.columnsTypes) {
      if(key.toLowerCase().includes(searchKey.toLowerCase())) {
        this.showColumnsTypes[key] = this.columnsTypes[key];
      }
    }
  }

  saveChangedColumnsType(): void {
    this.columnsTypes = [];
    for(const key in this.changedColumnsTypes) {
      if(key) {
        this.columnsTypes[key] = this.changedColumnsTypes[key];
      }
    }
  }

  save(): void {
    if(this.type === 'CreateDataSource') {
      this.isUploading = true;
      this.dataSourcePreviewFacade.createDataSourceTable(this.previewDataSourceInfo,
          this.workspaceId,this.columnsTypes,this.selectedSheetName);
    } else if(this.type === 'ReplacementDataSource') {
      this.
      dialogService.open(ConfirmationReplacementComponent, {
        closeOnBackdropClick: false,
        context: {
          oldDataSourceName: this.oldDataSourceName,
          previewDataSourceInfo: this.previewDataSourceInfo,
          workspaceId: this.workspaceId,
          columnsTypes: this.columnsTypes,
          sheetName: this.selectedSheetName,
          dataSourceWarnings: this.columns.dataSourceWarnings,
        },
      }).onClose.subscribe((isReplaced) => {
        if(isReplaced) {
          this.dialogRef.close();
        } else {
          this.deletePreviewConnection();
        }
      });
    }
  }

  setRowLength(data: any): void {
    this.columnsDataLength = [];
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        for(let index = 0 ; index < data[key].columnData.length; index++ ) {
          this.columnsDataLength.push(index);
        }
        break;
      }
    }
  }

  setColumnsType(data: any): void {
    this.columnsTypes = [];
    this.changedColumnsTypes = [];
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        this.columnsTypes[key] = data[key].columnType;
        this.changedColumnsTypes[key] = data[key].columnType;
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
